import React, { useCallback, useEffect, useState } from 'react';
import { IconFilter } from 'src/icons';
import { Popover } from '@headlessui/react';
import { useAdmin, ADMIN_DATA_CHANGED_EVENT } from 'src/admin';
import {
  getAdminUsersApi,
  adminResetPasswordApi,
  blockUserApi,
  unblockUserApi,
  type AdminUserRow,
} from 'src/services/api';

const STORAGE_USER = 'flower_power_notes_user';

type ActivityFilter = '' | 'online' | 'offline';
type StatusFilter = '' | 'active' | 'blocked';

const AdminUsers: React.FC = () => {
  const { refreshAdminData } = useAdmin();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActivity, setFilterActivity] = useState<ActivityFilter>('');
  const [contactMin, setContactMin] = useState<string>('');
  const [contactMax, setContactMax] = useState<string>('');
  const [filterSubscription, setFilterSubscription] = useState<string>('');
  const [createdFrom, setCreatedFrom] = useState<string>('');
  const [createdTo, setCreatedTo] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('');
  const [resetTarget, setResetTarget] = useState<AdminUserRow | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSaving, setResetSaving] = useState(false);
  const [blockSavingId, setBlockSavingId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const getAuthToken = () => {
    const raw = localStorage.getItem(STORAGE_USER);
    const user = raw ? JSON.parse(raw) : {};
    return user?.AuthToken ?? '';
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const result = await getAdminUsersApi(getAuthToken());
    setLoading(false);
    if (result.error) {
      setUsers([]);
      return;
    }
    setUsers(result.users ?? []);
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    const handler = () => loadUsers();
    window.addEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ADMIN_DATA_CHANGED_EVENT, handler);
  }, [loadUsers]);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase().trim();
    if (q) {
      const email = (u.email || '').toLowerCase();
      const name = (u.displayName || '').toLowerCase();
      const phone = (u.phone || '').toLowerCase();
      const id = String(u.id);
      if (!email.includes(q) && !name.includes(q) && !phone.includes(q) && !id.includes(q)) return false;
    }
    if (filterActivity === 'online' && !u.isOnline) return false;
    if (filterActivity === 'offline' && u.isOnline) return false;
    const c = u.contactCount ?? 0;
    const minC = contactMin.trim() === '' ? null : Number(contactMin);
    const maxC = contactMax.trim() === '' ? null : Number(contactMax);
    if (minC != null && !Number.isNaN(minC) && c < minC) return false;
    if (maxC != null && !Number.isNaN(maxC) && c > maxC) return false;
    if (filterSubscription && (u.subscriptionStatus || '').toLowerCase() !== filterSubscription.toLowerCase()) return false;
    if (createdFrom.trim() || createdTo.trim()) {
      const created = u.createdAt ? new Date(u.createdAt).getTime() : 0;
      const fromMs = createdFrom.trim() ? new Date(createdFrom.trim()).getTime() : null;
      const toMs = createdTo.trim() ? new Date(createdTo.trim() + 'T23:59:59.999').getTime() : null;
      if (fromMs != null && !Number.isNaN(fromMs) && created < fromMs) return false;
      if (toMs != null && !Number.isNaN(toMs) && created > toMs) return false;
    }
    if (filterStatus === 'active' && u.blockedAt) return false;
    if (filterStatus === 'blocked' && !u.blockedAt) return false;
    return true;
  });

  const hasActiveFilters =
    !!filterActivity || !!contactMin.trim() || !!contactMax.trim() || !!filterSubscription || !!createdFrom.trim() || !!createdTo.trim() || !!filterStatus;
  const clearFilters = () => {
    setFilterActivity('');
    setContactMin('');
    setContactMax('');
    setFilterSubscription('');
    setCreatedFrom('');
    setCreatedTo('');
    setFilterStatus('');
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !newPassword.trim() || newPassword.length < 6) {
      setResetError('Password must be at least 6 characters');
      return;
    }
    setResetError(null);
    setResetSaving(true);
    const result = await adminResetPasswordApi(getAuthToken(), {
      userId: resetTarget.id,
      newPassword: newPassword.trim(),
    });
    setResetSaving(false);
    if (result.error) {
      setResetError(result.error);
      return;
    }
    setResetTarget(null);
    setNewPassword('');
    setMessage('Password updated.');
    setTimeout(() => setMessage(null), 3000);
    await refreshAdminData();
  };

  const handleBlock = async (u: AdminUserRow) => {
    if (!window.confirm(`Block user "${u.displayName || u.email || u.phone || u.id}" (ID ${u.id})? They will not be able to log in or use the app.`)) return;
    setBlockSavingId(u.id);
    const result = await blockUserApi(getAuthToken(), u.id);
    setBlockSavingId(null);
    if (result.error) {
      setMessage('Error: ' + result.error);
      setTimeout(() => setMessage(null), 4000);
      return;
    }
    setMessage('User blocked.');
    setTimeout(() => setMessage(null), 3000);
    loadUsers();
    await refreshAdminData();
  };

  const handleUnblock = async (u: AdminUserRow) => {
    setBlockSavingId(u.id);
    const result = await unblockUserApi(getAuthToken(), u.id);
    setBlockSavingId(null);
    if (result.error) {
      setMessage('Error: ' + result.error);
      setTimeout(() => setMessage(null), 4000);
      return;
    }
    setMessage('User unblocked.');
    setTimeout(() => setMessage(null), 3000);
    loadUsers();
    await refreshAdminData();
  };

  return (
    <div className="flex flex-col flex-1 p-1.5 w-full min-h-0 overflow-hidden">
      <div className="flex flex-wrap justify-between items-center gap-1 mb-1 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="font-semibold text-gray-900 text-sm tracking-tight shrink-0">Users</h1>
          {message && <span className="text-green-600 text-xs truncate">{message}</span>}
        </div>
        <div className="flex flex-wrap flex-1 justify-end sm:justify-start items-center gap-1 min-w-0 sm:max-w-sm max-w-md">
          <input
            type="search"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-2 py-1 border border-gray-300 focus:border-sky-500 rounded focus:ring-1 focus:ring-sky-500 w-full min-w-0 text-xs"
            aria-label="Search users"
          />
          <Popover className="relative shrink-0">
            <Popover.Button
              className={`inline-flex items-center gap-1 px-2 py-1 border rounded text-xs font-medium transition-colors ${
                hasActiveFilters
                  ? 'border-sky-500 bg-sky-50 text-sky-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
              aria-label="Filter users"
            >
              <IconFilter className="w-3 h-3" />
              Filter
              {hasActiveFilters && (
                <span className="flex justify-center items-center bg-sky-500 rounded-full min-w-[12px] h-[12px] font-semibold text-white text-xs">•</span>
              )}
            </Popover.Button>
            <Popover.Panel className="right-0 z-50 absolute bg-white shadow-lg mt-1 border border-gray-200 rounded-lg w-[280px] max-h-[240px] overflow-y-auto">
              <div className="space-y-2 p-2">
                <div className="flex justify-between items-center pb-1 border-gray-100 border-b">
                  <span className="font-semibold text-gray-800 text-xs">Filters</span>
                  {hasActiveFilters && (
                    <button type="button" onClick={clearFilters} className="font-medium text-sky-600 hover:text-sky-700 text-xs">
                      Clear all
                    </button>
                  )}
                </div>
                <div>
                  <label htmlFor="filter-activity" className="block mb-0.5 font-medium text-gray-700 text-xs">Activity</label>
                  <select id="filter-activity" value={filterActivity} onChange={(e) => setFilterActivity(e.target.value as ActivityFilter)} className="px-1.5 py-1 border border-gray-300 rounded w-full text-xs" aria-label="Filter by activity">
                    <option value="">Any</option>
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-0.5 font-medium text-gray-700 text-xs">Contacts (min–max)</label>
                  <div className="flex items-center gap-1">
                    <input type="number" min={0} value={contactMin} onChange={(e) => setContactMin(e.target.value)} placeholder="Min" className="px-1.5 py-1 border border-gray-300 rounded w-full text-xs" aria-label="Contact min" />
                    <span className="text-gray-400 text-xs">–</span>
                    <input type="number" min={0} value={contactMax} onChange={(e) => setContactMax(e.target.value)} placeholder="Max" className="px-1.5 py-1 border border-gray-300 rounded w-full text-xs" aria-label="Contact max" />
                  </div>
                </div>
                <div>
                  <label htmlFor="filter-subscription" className="block mb-0.5 font-medium text-gray-700 text-xs">Subscription</label>
                  <select id="filter-subscription" value={filterSubscription} onChange={(e) => setFilterSubscription(e.target.value)} className="px-1.5 py-1 border border-gray-300 rounded w-full text-xs" aria-label="Subscription">
                    <option value="">Any</option>
                    <option value="active">Active</option>
                    <option value="canceled">Canceled</option>
                    <option value="past_due">Past due</option>
                    <option value="trialing">Trialing</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-0.5 font-medium text-gray-700 text-xs">Created</label>
                  <div className="flex gap-1">
                    <input type="date" value={createdFrom} onChange={(e) => setCreatedFrom(e.target.value)} className="px-1.5 py-1 border border-gray-300 rounded w-full text-xs" aria-label="From" />
                    <input type="date" value={createdTo} onChange={(e) => setCreatedTo(e.target.value)} className="px-1.5 py-1 border border-gray-300 rounded w-full text-xs" aria-label="To" />
                  </div>
                </div>
                <div>
                  <label htmlFor="filter-status" className="block mb-0.5 font-medium text-gray-700 text-xs">Status</label>
                  <select id="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as StatusFilter)} className="px-1.5 py-1 border border-gray-300 rounded w-full text-xs" aria-label="Status">
                    <option value="">Any</option>
                    <option value="active">Active</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
              </div>
            </Popover.Panel>
          </Popover>
        </div>
      </div>

      <div className="flex flex-col flex-1 bg-white border border-gray-200 rounded min-h-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-6">
            <p className="text-gray-500 text-xs">Loading…</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="relative divide-y divide-gray-200 min-w-full">
              <thead className="top-0 z-10 sticky bg-gray-50 shadow-[0_1px_0_0_rgba(0,0,0,0.05)]">
                <tr>
                  <th scope="col" className="bg-gray-50 px-2 py-1.5 font-semibold text-gray-700 text-xs text-left uppercase tracking-wider whitespace-nowrap">User</th>
                  <th scope="col" className="hidden sm:table-cell bg-gray-50 px-2 py-1.5 font-semibold text-gray-700 text-xs text-left uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th scope="col" className="hidden sm:table-cell bg-gray-50 px-2 py-1.5 font-semibold text-gray-700 text-xs text-left uppercase tracking-wider whitespace-nowrap">Sub</th>
                  <th scope="col" className="hidden md:table-cell bg-gray-50 px-2 py-1.5 font-semibold text-gray-700 text-xs text-left uppercase tracking-wider whitespace-nowrap">Contacts</th>
                  <th scope="col" className="hidden lg:table-cell bg-gray-50 px-2 py-1.5 font-semibold text-gray-700 text-xs text-left uppercase tracking-wider whitespace-nowrap">Last seen</th>
                  <th scope="col" className="hidden lg:table-cell bg-gray-50 px-2 py-1.5 font-semibold text-gray-700 text-xs text-left uppercase tracking-wider whitespace-nowrap">Created</th>
                  <th scope="col" className="bg-gray-50 px-2 py-1.5 font-semibold text-gray-700 text-xs text-right uppercase tracking-wider whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-2 py-4 text-gray-500 text-xs text-center">No users found.</td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5">
                        <div className="min-w-0">
                          {(() => {
                            const primary = u.email || u.phone || `#${u.id}`;
                            const secondary = primary === (u.email || '') ? (u.phone || '') : (u.email || '');
                            return (
                              <>
                                <span className="block font-medium text-gray-900 text-xs truncate">{primary}</span>
                                {secondary && <span className="block text-gray-500 text-xs truncate">{secondary}</span>}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-2 py-1.5">
                        {u.blockedAt ? (
                          <span className="inline-flex items-center bg-red-100 px-1 py-0.5 rounded font-medium text-red-800 text-xs">Blocked</span>
                        ) : (
                          <span className="inline-flex items-center bg-green-100 px-1 py-0.5 rounded font-medium text-green-800 text-xs">Active</span>
                        )}
                      </td>
                      <td className="hidden sm:table-cell px-2 py-1.5">
                        <span className={`text-xs ${u.subscriptionStatus === 'active' ? 'text-green-600' : 'text-gray-500'}`}>{u.subscriptionStatus || '–'}</span>
                      </td>
                      <td className="hidden md:table-cell px-2 py-1.5 text-gray-600 text-xs">{u.contactCount}</td>
                      <td className="hidden lg:table-cell px-2 py-1.5 text-xs">
                        {u.isOnline ? (
                          <span className="inline-flex items-center gap-0.5 font-medium text-green-600 text-xs">
                            <span className="bg-green-500 rounded-full w-1.5 h-1.5" aria-hidden /> Online
                          </span>
                        ) : u.lastSeenAt ? (
                          <span className="text-gray-500 text-xs">{new Date(u.lastSeenAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</span>
                        ) : (
                          <span className="text-gray-500">–</span>
                        )}
                      </td>
                      <td className="hidden lg:table-cell px-2 py-1.5 text-gray-500 text-xs">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '–'}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <button type="button" onClick={() => setResetTarget(u)} className="mr-1.5 font-medium text-sky-600 hover:text-sky-700 text-xs">
                          Reset PW
                        </button>
                        {u.blockedAt ? (
                          <button type="button" onClick={() => handleUnblock(u)} disabled={!!blockSavingId} className="disabled:opacity-50 font-medium text-green-600 hover:text-green-700 text-xs">
                            {blockSavingId === u.id ? '…' : 'Unblock'}
                          </button>
                        ) : (
                          <button type="button" onClick={() => handleBlock(u)} disabled={!!blockSavingId} className="disabled:opacity-50 font-medium text-amber-600 hover:text-amber-700 text-xs">
                            {blockSavingId === u.id ? '…' : 'Block'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resetTarget && (
        <div className="z-50 fixed inset-0 flex justify-center items-center bg-black/50 p-2">
          <div className="bg-white shadow-xl p-3 rounded-lg w-full max-w-xs">
            <h2 className="mb-1 font-semibold text-gray-900 text-xs">Reset password</h2>
            <p className="mb-2 text-gray-600 text-xs truncate">User: {resetTarget.displayName || resetTarget.email || resetTarget.phone || resetTarget.id}</p>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 6)"
              className="mb-2 px-2 py-1 border border-gray-300 rounded w-full text-xs"
              minLength={6}
            />
            {resetError && <p className="mb-1 text-red-600 text-xs">{resetError}</p>}
            <div className="flex justify-end gap-1">
              <button
                type="button"
                onClick={() => { setResetTarget(null); setNewPassword(''); setResetError(null); }}
                className="hover:bg-gray-50 px-2 py-1 border border-gray-300 rounded font-medium text-gray-700 text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetPassword}
                disabled={resetSaving || newPassword.length < 6}
                className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 px-2 py-1 rounded font-medium text-white text-xs"
              >
                {resetSaving ? '…' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
